// Client-side export helpers (CSV / Print / PDF via print)

export function toCSV(title, rows) {
  const escape = (v) => {
    const s = String(v ?? "");
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const lines = [];
  lines.push(`# ${title}`);
  lines.push(`# Generated: ${new Date().toLocaleString()}`);
  lines.push("Label,Value");
  for (const [k, v] of rows) lines.push(`${escape(k)},${escape(v)}`);
  return lines.join("\n");
}

export function downloadFile(filename, content, mime = "text/plain") {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function exportCSV(title, rows) {
  const safe = title.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  downloadFile(`${safe}-${Date.now()}.csv`, toCSV(title, rows), "text/csv");
}

export function printPDF(title, rows, note) {
  const win = window.open("", "_blank", "width=800,height=900");
  if (!win) return;
  const doc = win.document;

  // Build DOM via safe APIs (no innerHTML/document.write) to eliminate XSS risk.
  doc.title = `${title} — LandCalc`;

  const style = doc.createElement("style");
  style.textContent = `body{font-family:Inter,sans-serif;max-width:720px;margin:32px auto;padding:24px;color:#0a0a0a}
    h1{font-size:24px;margin:0 0 4px;letter-spacing:-0.02em}
    .muted{color:#525252;font-size:12px;margin-bottom:24px}
    table{width:100%;border-collapse:collapse;margin-top:16px}
    td{padding:8px 12px;border-bottom:1px solid #e5e7eb}
    td.label{color:#525252}
    td.value{text-align:right;font-family:JetBrains Mono,monospace;font-weight:600}
    .brand{color:#0F7A3A;font-weight:800;letter-spacing:0.02em}
    .note{margin-top:24px;padding:12px 16px;background:#f8faf9;border-left:3px solid #0F7A3A;font-size:12px;color:#525252}`;
  doc.head.appendChild(style);

  const body = doc.body;
  const brand = doc.createElement("div"); brand.className = "brand"; brand.textContent = "LANDCALC"; body.appendChild(brand);
  const h1 = doc.createElement("h1"); h1.textContent = title; body.appendChild(h1);
  const meta = doc.createElement("div"); meta.className = "muted"; meta.textContent = `Generated on ${new Date().toLocaleString()}`; body.appendChild(meta);

  const table = doc.createElement("table");
  for (const [k, v] of rows) {
    const tr = doc.createElement("tr");
    const tdK = doc.createElement("td"); tdK.className = "label"; tdK.textContent = String(k); tr.appendChild(tdK);
    const tdV = doc.createElement("td"); tdV.className = "value"; tdV.textContent = String(v); tr.appendChild(tdV);
    table.appendChild(tr);
  }
  body.appendChild(table);

  if (note) {
    const n = doc.createElement("div"); n.className = "note"; n.textContent = note; body.appendChild(n);
  }

  win.focus();
  setTimeout(() => win.print(), 400);
}

export async function copyResults(rows) {
  const text = rows.map(([k, v]) => `${k}: ${v}`).join("\n");
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export async function shareResults(title, rows) {
  const text = rows.map(([k, v]) => `${k}: ${v}`).join("\n");
  if (navigator.share) {
    try {
      await navigator.share({ title: `${title} — LandCalc`, text });
      return true;
    } catch {
      return false;
    }
  }
  return copyResults(rows);
}
